// The order a house gives one of its heroes.
//
// Two orders exist: *slay N foes* and *hunt this enemy hero*. The dialog
// only asks; the sim decides (`World::dispatch_hero_quest`), and refuses
// anything that is not the caller's hero to order.

import { Button } from '@aod/ui/components/ui/button';
import { Input } from '@aod/ui/components/ui/input';
import { SceneDialog } from '@aod/ui/components/ui/scene-dialog';
import { useState } from 'react';
import type { HeroQuestKind, HuntTarget } from '../../heroes/quests.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';

export interface HeroQuestDialogProps {
  /** The hero being ordered. */
  readonly heroName: string;
  /** The enemy heroes the client can see — one button each. */
  readonly huntTargets: readonly HuntTarget[];
  /** Give the order. `targetUnitId` is the hero to hunt, for a hunt. */
  readonly onOrder: (kind: HeroQuestKind, amount?: number, targetUnitId?: string) => void;
  readonly onClose: () => void;
}

/** How many foes a `slay` order asks for when the player does not choose. */
const DEFAULT_AMOUNT = 3;
/** The most a `slay` order may ask for. */
const MAX_AMOUNT = 20;

export function HeroQuestDialog({ heroName, huntTargets, onOrder, onClose }: HeroQuestDialogProps) {
  const t = useT();
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const chosen = Math.min(MAX_AMOUNT, Math.max(1, amount));

  return (
    <SceneDialog
      onClose={onClose}
      title={t('heroes.quest.dialog.title', { name: heroName })}
      description={t('heroes.quest.dialog.body')}
      data-testid="hero-quest-dialog"
      {...appDom({ id: 'hero-quest-dialog', type: 'Modal', scope: 'dialog' })}
      footer={
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t('heroes.quest.dialog.cancel')}
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('heroes.quest.dialog.slayLabel')}
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={MAX_AMOUNT}
              value={amount}
              aria-label={t('heroes.quest.dialog.slayLabel')}
              data-testid="hero-quest-slay-amount"
              onChange={(e) => setAmount(Number(e.target.value) || DEFAULT_AMOUNT)}
              className="h-8 w-20 px-2 text-xs"
            />
            <Button
              type="button"
              size="sm"
              data-testid="hero-quest-slay"
              {...appDom({
                id: 'hero-quest-slay',
                type: 'Button',
                role: 'button',
                scope: 'dialog',
              })}
              onClick={() => onOrder('slay', chosen)}
            >
              {t('heroes.order.slay', { n: chosen })}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('heroes.order.kind.hunt')}
          </span>
          {huntTargets.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              {t('heroes.quest.dialog.noTargets')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {huntTargets.map((target) => (
                <button
                  key={target.unitId}
                  type="button"
                  data-testid={`hero-quest-hunt-${target.unitId}`}
                  {...appDom({
                    id: `hero-quest-hunt-${target.unitId}`,
                    type: 'Button',
                    role: 'button',
                    scope: 'dialog',
                  })}
                  onClick={() => onOrder('hunt', 1, target.unitId)}
                  className="rounded border border-border/60 bg-card/60 px-2 py-1 text-[11px] text-foreground hover:bg-muted/60"
                >
                  {t('heroes.order.hunt', { name: target.name })}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </SceneDialog>
  );
}
